import type { PropsWithChildren, ReactNode } from 'react';
import React, { useCallback } from 'react';
import { Divider } from 'antd';
import type { GlobalToken } from 'antd/es/theme/interface';
import {
  AnchorButton,
  ButtonVariant,
  LinkButton,
} from '../../../component_library/components/Button';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import type { ThemeColor } from '../../../component_library/styles/theme';
import { useRedirectToExternalUrl } from '../../../hooks/useRedirectToExternalUrl';
import { GLOBAL_BUTTON_STYLES } from '../../../component_library/components/Button/button-style-helper';

interface IProps extends IPropsWithTestId {
  themeToken: GlobalToken;
  loginUrl: string;
  isExternalUrl: boolean;
  iconUrl?: string;
  color?: ThemeColor;
  buttonVariant?: ButtonVariant;
}

const LoginProviderButton: React.FC<PropsWithChildren<IProps>> = ({
  children,
  themeToken,
  loginUrl,
  isExternalUrl,
  dataElementId,
  iconUrl,
  color,
  buttonVariant = color ? ButtonVariant.PRIMARY : ButtonVariant.SECONDARY,
}) => {
  const { redirectToExternalUrl } = useRedirectToExternalUrl();

  const handleExternalLoginClick = useCallback(
    () => redirectToExternalUrl(loginUrl),
    [loginUrl, redirectToExternalUrl],
  );

  const renderButton = (innerContent: ReactNode) =>
    (isExternalUrl ? (
      <AnchorButton
        href={loginUrl}
        onClick={handleExternalLoginClick}
        target="_self"
        dataElementId={dataElementId}
        themeToken={themeToken}
        variant={buttonVariant}
        color={color}
      >
        {innerContent}
      </AnchorButton>
    ) : (
      <LinkButton
        to={loginUrl}
        dataElementId={dataElementId}
        themeToken={themeToken}
        variant={buttonVariant}
        color={color}
      >
        {innerContent}
      </LinkButton>
    ));

  return renderButton(
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        ...GLOBAL_BUTTON_STYLES,
      }}
    >
      {!!iconUrl && (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <img src={iconUrl} alt="" height="100%" width="100%" />
          <Divider
            type="vertical"
            style={{
              height: 'inherit',
              borderColor: '#F0F0F0',
              margin: '0 16px',
            }}
          />
        </div>
      )}
      <div
        style={{
          width: '100%',
        }}
      >
        {children}
      </div>
    </div>,
  );
};

export default LoginProviderButton;
